// src/views/Seguridad/Pines/EditarPines.jsx
import React, { useEffect, useMemo, useState } from "react";
import apiRoutes from "../../../api/apiRoutes";
import { apiFetchJson, appFetchJson } from "../../../utils/http";
import BtnGuardar from "../../../components/buttons/BtnGuardar";
import BtnCancelar from "../../../components/buttons/BtnCancelar";

/**
 * Props:
 * - id: ID de la Liquidación a editar
 * - onClose / onVolver: () => void
 * - onSaved: () => void
 */
export default function EditarEjecucionDeLiquidacion({
  id,
  onClose,
  onVolver,
  onSaved,
}) {
  const closeFn = onClose || onVolver || (() => {});
  const savedFn = onSaved || (() => {});

  // ============================
  //        ESTADOS
  // ============================
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Snapshot original
  const [original, setOriginal] = useState(null);

  // ===== EDITABLE =====
  const [descripcion, setDescripcion] = useState("");

  // ============================
  //        CERRAR CON ESC
  // ============================
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && closeFn();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeFn]);

  // ============================
  //        CARGA INICIAL
  // ============================
  useEffect(() => {
    if (!id) return;

    const load = async () => {
      setLoading(true);
      setError("");

      try {
        const dto = await apiFetchJson(apiRoutes.liquidacion.detail(id));

        setOriginal(dto);
        setDescripcion(dto.descripcion ?? "");
      } catch (e) {
        setError(e?.message || "Error al cargar la Liquidación");
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id]);

  // ============================
  //     DETECTAR CAMBIOS
  // ============================
  const changed = useMemo(() => {
    if (!original) return false;
    return descripcion !== (original.descripcion ?? "");
  }, [original, descripcion]);

  // ============================
  //        GUARDAR
  // ============================
  const handleSave = async () => {
    if (!changed || !id) return;

    setSaving(true);
    setError("");

    try {
      await appFetchJson(apiRoutes.liquidacion.update(id), {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          descripcion: descripcion.trim(),
        }),
      });

      savedFn();
      closeFn();
    } catch (e) {
      setError(e?.message || "Ocurrió un error al guardar");
    } finally {
      setSaving(false);
    }
  };

  // ============================
  //        RENDER
  // ============================
  if (loading) {
    return (
      <div className="modal-backdrop">
        <div className="modal-panel-lg">
          <div className="modal-body">Cargando…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-backdrop" onClick={closeFn}>
      <div className="modal-panel-lg" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="modal-header">
          <h2 className="section-title">
            Introduzca los datos de Liquidación
          </h2>
        </div>

        {/* Body */}
        <div className="modal-body">
          {error && <div className="mb-3 text-red-600">{error}</div>}

          <div className="form-grid">
            <FilaEdit
              label="Descripción"
              input={
                <input
                  className="input"
                  value={descripcion}
                  onChange={(e) => setDescripcion(e.target.value)}
                  maxLength={60}
                />
              }
            />
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <div className="btn-row">
            <BtnGuardar
              isValid={changed}
              saving={saving}
              onClick={handleSave}
            />
            <BtnCancelar onClick={closeFn} />
          </div>
        </div>
      </div>
    </div>
  );
}

/* =========================
   SUBCOMPONENTES
========================= */

function FilaEdit({ label, input }) {
  return (
    <div className="form-row gap-2 items-start">
      <div className="form-label">{label}:</div>
      <div className="flex-1">{input}</div>
    </div>
  );
}
